import { useState } from 'react'
import { SearchFilters } from '../types/property'

interface FiltersModalProps {
  filters: SearchFilters
  onClose: () => void
  onApply: (filters: SearchFilters) => void
}

export default function FiltersModal({ filters, onClose, onApply }: FiltersModalProps) {
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters)

  const propertyTypes = [
    'Residential',
    'Condo',
    'Townhouse',
    'Multi-Family',
    'Land',
    'Commercial',
  ]

  const handleApply = () => {
    onApply(localFilters)
  }

  const handleReset = () => {
    setLocalFilters({})
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">More Filters</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Property Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {propertyTypes.map(type => (
                <label key={type} className="flex items-center gap-2 p-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localFilters.propertyType?.includes(type) || false}
                    onChange={(e) => {
                      const current = localFilters.propertyType || []
                      setLocalFilters({
                        ...localFilters,
                        propertyType: e.target.checked
                          ? [...current, type]
                          : current.filter(t => t !== type)
                      })
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Square Footage */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Sqft
              </label>
              <input
                type="number"
                value={localFilters.minSqft || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, minSqft: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Sqft
              </label>
              <input
                type="number"
                value={localFilters.maxSqft || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, maxSqft: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          {/* Year Built */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Year Built
              </label>
              <input
                type="number"
                value={localFilters.minYear || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, minYear: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Year Built
              </label>
              <input
                type="number"
                value={localFilters.maxYear || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, maxYear: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          {/* Lot Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Lot Size (acres)
              </label>
              <input
                type="number"
                step="0.1"
                value={localFilters.minLot || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, minLot: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Lot Size (acres)
              </label>
              <input
                type="number"
                step="0.1"
                value={localFilters.maxLot || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, maxLot: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  )
}
